import { css, html, LitElement } from '~/lib/bn-lit-element';
import { renderIf } from '../utility/lit-entity';
import { loadGoogleMapsAPI } from '../libs/google-maps-api';
import { LitTraceMixin } from '../utility/lit-trace-mixin';
import { sharedStyles } from '../styles/shared-styles';
import { bannerStyles } from '../styles/banner-styles';
import './bn-map-marker';
import './bn-icon';

const config = JSON.parse(import.meta.env.VITE_PROJECT_AWS_SSM_CONFIG);

class BnMap extends LitTraceMixin(LitElement) {
  constructor() {
    super();
    this.mapType = 'hybrid';
    this.zoom = 8;
    this.fullscreenControl = true;
    this.mapTypeControl = true;
    this.rotateControl = false;
    this.scaleControl = false;
    this.streetViewControl = false;
    this.zoomControl = true;
  }

  static get properties() {
    return {
      googleMapsKey: { type: String, attribute: 'google-maps-key' },
      directions: { type: Boolean },
      directionsError: { type: String },
      mapType: { type: String, attribute: 'map-type' },
      zoom: { type: Number },
      // controls
      fullscreenControl: { type: Boolean },
      mapTypeControl: { type: Boolean },
      rotateControl: { type: Boolean },
      scaleControl: { type: Boolean },
      streetViewControl: { type: Boolean },
      zoomControl: { type: Boolean },
    };
  }

  static get styles() {
    return [sharedStyles, bannerStyles, css`
      :host {
        display: block;
        position: relative;
      }
      #mapContainer {
        height: 100%;
        width: 100%;
      }
      #directionsPanel {
        position: absolute;
        bottom: 1rem;
        left: 1rem;
        top: 1rem;
        right: 1rem;
        overflow: auto;
        font-size: .8rem;
        background-color: var(--surface);
        display: none;
      }
      .error {
        position: absolute;
        top: 3rem;
        left: 1rem;
        right: 1rem;
        z-index: 10;
        display: flex;
        align-items: center;
      }
      .error p {
        flex-grow: 1;
      }
      .error bn-icon {
        margin-right: .5rem;
        font-size: 1.5rem;
      }
    `];
  }

  update(changedAttributes) {
    if (changedAttributes.has('zoom')) {
      this._zoomChanged();
    }
    super.update(changedAttributes);
  }

  render() {
    return html`
      ${renderIf(this.directionsError, () => html`
        <div class="error banner banner--critical">
          <bn-icon name="fas fa-exclamation-circle"></bn-icon>
          <p>${this.directionsError}</p>
          <button class="button button--plain" @click="${() => this.directionsError = undefined}"><bn-icon name="far fa-times"></bn-icon></button>
        </div>
      `)}
      <div id="mapContainer"></div>
      ${renderIf(this.directions, html`
        <div id="directionsPanel"></div>
      `)}
      <slot @slotchange="${this._slotChange}"></slot>
    `;
  }

  firstUpdated() {
    super.firstUpdated();
    this._initMap();
  }

  updateSlottedMarkers() {
    this.__trace('update slotted markers');
    if (!this._mapRef) {
      return;
    }
    const slot = this.shadowRoot.querySelector('slot');
    const nodes = slot.assignedNodes().filter((node) => ['BN-MAP-MARKER', 'BIN-MAP-MARKER'].includes(node.nodeName));
    nodes.forEach((n) => n.map = this._mapRef);
    this._markers = nodes.map((n) => n.marker);
    this.fitMarkers();
    if (this.directions) {
      this._route();
    }
  }

  _slotChange() {
    this.__trace('slot changed');
    this.updateSlottedMarkers();
  }

  async _initMap() {
    this.__trace('init map');
    const maps = await loadGoogleMapsAPI({ apiKey: this.googleMapsKey || config['google_maps_key'] });
    let mapTypeId;
    const { MapTypeId } = google.maps;
    switch (this.mapType) {
    case 'roadmap':
      mapTypeId = MapTypeId.ROADMAP;
      break;
    case 'satallite':
      mapTypeId = MapTypeId.SATELLITE;
      break;
    case 'hybrid':
    default:
      mapTypeId = MapTypeId.HYBRID;
      break;
    }
    this._mapRef = new maps.Map(this.shadowRoot.querySelector('#mapContainer'), {
      center: { lat: 43.3758953, lng: -80.6966608 },
      mapTypeId,
      zoom: this.zoom,
      tilt: 0,
      fullscreenControl: this.fullscreenControl,
      mapTypeControl: this.mapTypeControl,
      mapTypeControlOptions: {
        mapTypeIds: [
          MapTypeId.ROADMAP,
          MapTypeId.SATELLITE,
        ],
      },
      rotateControl: this.rotateControl,
      scaleControl: this.scaleControl,
      streetViewControl: this.streetViewControl,
      styles: [
        {
          featureType: 'poi',
          stylers: [
            { visibility: 'off' },
          ],
        },
      ],
      zoomControl: this.zoomControl,
    });

    this.directionsService = new google.maps.DirectionsService;
    this.directionsDisplay = new google.maps.DirectionsRenderer({
      suppressMarkers: true,
      preserveViewport: true,
    });
    this.directionsDisplay.setMap(this._mapRef);
    this.directionsDisplay.setPanel(this.shadowRoot.getElementById('directionsPanel'));

    this.updateSlottedMarkers();
  }

  _route() {
    if (this._markers.length < 2) {
      this.directionsDisplay.set('directions', null);
      return;
    }
    const waypoints = [];
    for (const marker of this._markers) {
      waypoints.push({
        location: marker.position,
        stopover: true,
      });
    }
    const first = this._markers[0].position;
    const last = this._markers[this._markers.length - 1].position;
    this.directionsService.route({
      origin: first,
      destination: last,
      waypoints,
      optimizeWaypoints: false,
      travelMode: 'DRIVING',
    },
    (response, status) => {
      if (status !== 'OK') {
        this.directionsError = 'Unable to generate directions.';
        return;
      }
      this.directionsDisplay.setDirections(response);
      this.dispatchEvent(new CustomEvent('routeresponse', { detail: { response }, bubbles: true, composed: true }));
    });
  }

  _zoomChanged() {
    this._mapRef && this._mapRef.setZoom(this.zoom);
  }

  fitMarkers() {
    this.__trace('fit markers');
    if (!this._mapRef || this._markers.length <= 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    for (const marker of this._markers) {
      bounds.extend(marker.getPosition());
    }

    if (this._markers.length > 1) {
      // Don't zoom in too far when all markers at the same point
      if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
        bounds.extend(new google.maps.LatLng(bounds.getNorthEast().lat() + 0.01, bounds.getNorthEast().lng() + 0.01));
        bounds.extend(new google.maps.LatLng(bounds.getNorthEast().lat() - 0.01, bounds.getNorthEast().lng() - 0.01));
      }
      const padding = 100;
      this._mapRef.fitBounds(bounds, padding);
    }

    this._mapRef.setCenter(bounds.getCenter());
  }
}

window.customElements.define('bn-map', BnMap);
